import { defineStore } from 'pinia';
import { jwtDecode } from 'jwt-decode';
import api from '@/services/api'; // Importa a instância do axios/API
import { clearApiAuthState } from '@/services/api';
import { useCustomerStore } from '@/stores/customerStore';
import { useWithdrawalStore } from '@/stores/withdrawalStore';
import { useCartStore } from '@/stores/cartStore';

const ACTIVATION_NEXT_RESEND_AT_KEY = 'activation_next_resend_at';
const ACCESS_TOKEN_SESSION_KEY = 'presentei_at_v1';

const LOGOUT_LOCAL_STORAGE_KEYS = [
  ACTIVATION_NEXT_RESEND_AT_KEY,
  'hideBankDetailsWarning',
  'forgotPasswordState',
  'presentei_customers_v2',
  'presentei_customer',
  'presentei_withdrawal_v1',
  'balance_overview_data_cache',
  'pix_details_cache',
  'rescue_details_cache',
  'cachedEventTypes',
  'cart_items',
  'isBalanceHidden',
  'user',
];

const LOGOUT_LOCAL_STORAGE_PREFIXES = [
  'pixData-',
  'cardForm-',
  'cardPaymentId-',
];

const LOGOUT_SESSION_STORAGE_KEYS = [
  'register_data',
  ACCESS_TOKEN_SESSION_KEY,
];

function removeStorageKeys(storage, keys = []) {
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      // silencioso
    }
  }
}

function removeStorageByPrefixes(storage, prefixes = []) {
  try {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (!key) continue;

      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        storage.removeItem(key);
      }
    }
  } catch {
    // silencioso
  }
}

function clearPersistedSessionArtifacts() {
  removeStorageKeys(localStorage, LOGOUT_LOCAL_STORAGE_KEYS);
  removeStorageByPrefixes(localStorage, LOGOUT_LOCAL_STORAGE_PREFIXES);
  removeStorageKeys(sessionStorage, LOGOUT_SESSION_STORAGE_KEYS);
}

function readStoredActivationNextResendAt() {
  const rawValue = localStorage.getItem(ACTIVATION_NEXT_RESEND_AT_KEY);
  if (!rawValue) return null;

  const parsedNumber = Number(rawValue);
  if (!Number.isNaN(parsedNumber) && Number.isFinite(parsedNumber) && parsedNumber > 0) {
    return parsedNumber;
  }

  const parsedDate = Date.parse(rawValue);
  if (!Number.isNaN(parsedDate) && parsedDate > 0) {
    return parsedDate;
  }

  return null;
}

const ACTIVATION_FLAG_CANDIDATES = [
  'isActivated',
  'is_activated',
  'emailVerified',
  'email_verified',
];

const FINANCIAL_FLAG_CANDIDATES = [
  'canReceiveTransfers',
  'can_receive_transfers',
  'isFinanciallyEnabled',
  'is_financially_enabled',
];

const PROVIDER_ACCOUNT_STATUS_CANDIDATES = [
  'provider_account_status',
  'providerAccountStatus',
];

function normalizePlatformStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'active' || normalized === 'enabled') {
    return 'active';
  }

  if (normalized === 'in_review') {
    return 'pending';
  }

  if (normalized === 'restricted' || normalized === 'pending') {
    return 'restricted';
  }

  if (normalized === 'review_pending') {
    return 'pending';
  }

  if (normalized === 'pending_review') {
    return 'pending';
  }

  if (normalized === 'under_review') {
    return 'pending';
  }

  if (normalized === 'approved') {
    return 'active';
  }

  if (normalized === 'declined' || normalized === 'rejected') {
    return 'restricted';
  }

  return '';
}

function findBooleanFlag(source, candidates) {
  if (!source || typeof source !== 'object') {
    return { found: false, value: false, field: null };
  }

  for (const field of candidates) {
    if (Object.prototype.hasOwnProperty.call(source, field) && typeof source[field] === 'boolean') {
      return { found: true, value: source[field], field };
    }
  }

  return { found: false, value: false, field: null };
}

function findPlatformStatusFlag(source, candidates) {
  if (!source || typeof source !== 'object') {
    return { found: false, value: '', field: null };
  }

  for (const field of candidates) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      const normalized = normalizePlatformStatus(source[field]);
      if (normalized) {
        return { found: true, value: normalized, field };
      }
    }
  }

  return { found: false, value: '', field: null };
}

function collectFlagSources(payload) {
  if (!payload || typeof payload !== 'object') return [];
  return [
    payload,
    payload.user,
    payload.profile,
    payload.data,
    payload.data?.user,
    payload.data?.profile,
  ].filter(Boolean);
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null,
    user: null,
    authReady: false,
    
    bankDetailsRegistered: false, 
    bankDetailsLoading: false,    
    bankDetailsError: false,      
    bankDetailsData: null,        

    profileLoading: false, // Indica se o perfil está sendo carregado
    profileLoaded: false,  // Vai para true após fetchUserProfile() completar (sucesso ou erro)
    profileError: null,    // Armazena erros no carregamento/atualização do perfil
    profileSaving: false,  // Indica se o perfil está sendo salvo

    isOnboardingMode: false, // Ativo quando usuário acabou de se cadastrar e deve completar o perfil
    isActivated: true,
    activationStatusLoaded: false,
    activationFlagField: null,
    platformStatus: 'restricted',
    platformStatusLoaded: false,
    platformStatusField: null,
    financialEligibilityLoaded: false,
    canReceiveTransfers: false,
    financialEligibilityField: null,
    activationNextResendAt: readStoredActivationNextResendAt(),
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isAdmin: (state) => {
      const role = state.user?.role ?? '';
      return role === 'admin' || role === 'finance';
    },
    isProfileComplete: (state) => {
      const u = state.user;
      return !!(u?.name && u?.phone);
    },
    isActivationPending: (state) => state.isAuthenticated && state.platformStatusLoaded && state.platformStatus !== 'active',
    normalizedPlatformStatus: (state) => state.platformStatus || 'restricted',
    isFinanciallyEnabled: (state) => {
      return state.bankDetailsRegistered;
    },
    activationResendRemainingSeconds: (state) => {
      if (!state.activationNextResendAt) return 0;
      const remainingMs = state.activationNextResendAt - Date.now();
      if (remainingMs <= 0) return 0;
      return Math.ceil(remainingMs / 1000);
    },
  },

  actions: {
    setActivationNextResendAt(nextResendAt) {
      if (!nextResendAt) {
        this.activationNextResendAt = null;
        localStorage.removeItem(ACTIVATION_NEXT_RESEND_AT_KEY);
        return;
      }

      const normalized = typeof nextResendAt === 'number'
        ? nextResendAt
        : Date.parse(String(nextResendAt));

      if (Number.isNaN(normalized) || !Number.isFinite(normalized) || normalized <= 0) {
        return;
      }

      this.activationNextResendAt = normalized;
      localStorage.setItem(ACTIVATION_NEXT_RESEND_AT_KEY, String(normalized));
    },

    clearActivationResendCooldown() {
      this.activationNextResendAt = null;
      localStorage.removeItem(ACTIVATION_NEXT_RESEND_AT_KEY);
    },

    applyBackendEligibilityFlags(payload) {
      const sources = collectFlagSources(payload);

      for (const source of sources) {
        const platformStatus = findPlatformStatusFlag(source, PROVIDER_ACCOUNT_STATUS_CANDIDATES);
        if (platformStatus.found) {
          this.platformStatus = platformStatus.value;
          this.platformStatusLoaded = true;
          this.platformStatusField = platformStatus.field;
          this.isActivated = platformStatus.value === 'active';
          this.activationStatusLoaded = true;
          this.activationFlagField = 'provider_account_status';
          break;
        }
      }

      for (const source of sources) {
        const activation = findBooleanFlag(source, ACTIVATION_FLAG_CANDIDATES);
        if (activation.found) {
          this.isActivated = activation.value;
          this.activationStatusLoaded = true;
          this.activationFlagField = activation.field;
          break;
        }
      }

      for (const source of sources) {
        const financial = findBooleanFlag(source, FINANCIAL_FLAG_CANDIDATES);
        if (financial.found) {
          this.canReceiveTransfers = financial.value;
          this.financialEligibilityField = financial.field;
          this.financialEligibilityLoaded = true;
          break;
        }
      }

      if (this.platformStatusLoaded && !this.financialEligibilityLoaded) {
        this.canReceiveTransfers = this.platformStatus === 'active';
        this.financialEligibilityLoaded = true;
        this.financialEligibilityField = 'provider_account_status';
      }
    },

    setToken(token) {
      this.token = token;
      if (token) {
        try { sessionStorage.setItem(ACCESS_TOKEN_SESSION_KEY, token); } catch { /* privado/SSR */ }
      } else {
        try { sessionStorage.removeItem(ACCESS_TOKEN_SESSION_KEY); } catch { /* privado/SSR */ }
      }
    },

    setTokenAndDecodeUser(token, payload = null) {
      this.setToken(token);

      try {
        const decoded = jwtDecode(token);
        this.user = {
          id: decoded.id,
          name: decoded.name,
          fullName: decoded.name, // Assumindo que 'name' é o nome completo, ajuste se necessário
          role: decoded.role 
        };

        this.applyBackendEligibilityFlags(decoded);
        this.applyBackendEligibilityFlags(payload);

        this.fetchBankDetailsStatus();
        this.fetchUserProfile(); // ← carrega dados de perfil da API

      } catch (error) {
        console.error("Erro ao decodificar JWT:", error);
        this.logout();
      }
    },

    logout() {
      clearApiAuthState();

      this.token = null;
      this.user = null;
      
      this.bankDetailsRegistered = false; 
      this.bankDetailsLoading = false;
      this.bankDetailsError = false;
      this.bankDetailsData = null;
      this.isActivated = true;
      this.activationStatusLoaded = false;
      this.activationFlagField = null;
      this.financialEligibilityLoaded = false;
      this.canReceiveTransfers = false;
      this.financialEligibilityField = null;
      this.clearActivationResendCooldown();

      const customerStore = useCustomerStore();
      const withdrawalStore = useWithdrawalStore();
      const cartStore = useCartStore();

      clearPersistedSessionArtifacts();

      customerStore.clearAll();
      withdrawalStore.$reset();
      cartStore.$reset();

      api.post('/auth/logout', {}, { withCredentials: true }).catch(() => {});
    },

    async initializeAuth() {
      localStorage.removeItem('user');

      try {
        if (!this.token) {
          // 1. Tenta restaurar do sessionStorage (sobrevive a F5 na mesma aba)
          try {
            const cached = sessionStorage.getItem(ACCESS_TOKEN_SESSION_KEY);
            if (cached) {
              const decoded = jwtDecode(cached);
              const nowSecs = Math.floor(Date.now() / 1000);
              if (decoded.exp > nowSecs + 60) {
                // Token ainda válido (com 1 min de margem) — restaura sem chamada de rede
                this.token = cached;
                this.user = {
                  id: decoded.id,
                  name: decoded.name,
                  fullName: decoded.name,
                  role: decoded.role,
                };
                this.applyBackendEligibilityFlags(decoded);
                this.fetchBankDetailsStatus();
                this.fetchUserProfile();
                return;
              }
            }
          } catch {
            // sessionStorage bloqueado ou token malformado — ignora e segue para o cookie
          }
          sessionStorage.removeItem(ACCESS_TOKEN_SESSION_KEY);

          // 2. Fallback: usa o refresh token (cookie HttpOnly) para obter novo access token
          try {
            const response = await api.get('/auth/me');
            if (response.data?.token) {
              this.setTokenAndDecodeUser(response.data.token, response.data);
            }
          } catch {
            this.token = null;
            this.user = null;
          }
          return;
        }

        this.fetchBankDetailsStatus();
        this.fetchUserProfile();
      } finally {
        this.authReady = true;
      }
    },

    setOnboardingMode(value) {
      this.isOnboardingMode = value;
    },

    async fetchBankDetailsStatus() {
      this.platformStatus = 'restricted';
      this.platformStatusLoaded = false;
      this.platformStatusField = null;
      if (!this.isAuthenticated || !this.user?.id) {
        this.bankDetailsRegistered = false;
        this.bankDetailsLoading = false;
        this.bankDetailsError = false;
        this.bankDetailsData = null;
        return;
      }

      this.bankDetailsLoading = true;
      this.bankDetailsError = false;

      try {
        const response = await api.get('/users/finance/connected-accounts/stripe/active-bank-details');
        const payload = response?.data || {};

        this.bankDetailsData = payload;
        this.bankDetailsRegistered = Boolean(payload?.has_bank ?? payload?.hasBank);
        this.bankDetailsError = false;
      } catch (error) {
        this.bankDetailsData = null;
        this.bankDetailsRegistered = false;
        const status = error?.response?.status;
        this.bankDetailsError = !status || status >= 500;
      } finally {
        this.bankDetailsLoading = false;
      }
    },

        async saveBankDetails(payload) {
        if (!this.isAuthenticated || !this.user?.id) {
            console.error('Auth Store: Usuário não autenticado ou ID ausente para salvar dados bancários.');
            return false;
        }

        this.bankDetailsLoading = true;
        // Não alteramos bankDetailsError aqui, ele deve refletir o status de *carregamento*

        try {
            const userId = this.user.id;
            const response = await api.put(`/users/${userId}/bank-details`, payload);
            
            this.bankDetailsData = response.data || payload;
            this.bankDetailsRegistered = true;
            this.bankDetailsError = false;

            // Re-fetcha da API para garantir sincronização com o estado real
            await this.fetchBankDetailsStatus();

            return true; 
        } catch (error) {
            console.error('Auth Store: Erro ao salvar dados bancários:', error);
            // **** AQUI ESTÁ A MUDANÇA CRUCIAL APLICADA ****
            // NÃO DEVE ALTERAR bankDetailsRegistered, bankDetailsError ou localStorage para um erro de senha (401)
            // Esses estados devem ser gerenciados pela lógica de *carregamento* inicial dos dados.
            // A propagação do erro (throw error) é suficiente para o componente ProfileDetails.vue lidar com a mensagem específica.
            
            throw error; // Propaga o erro para o componente lidar com a mensagem específica (toast, erro no campo)
        } finally {
            this.bankDetailsLoading = false;
        }
    },

    async fetchUserProfile() {
      this.profileLoading = true;
      this.profileError = null;
      try {
        const response = await api.get(`/users/${this.user.id}/profile`); // Assumindo que esta API retorna o phone no formato +55...
        
        let fetchedUser = response.data.user || response.data.profile || response.data;
        
        // Normaliza document → cpf_document
        if (fetchedUser.document && !fetchedUser.cpf_document) {
          fetchedUser.cpf_document = fetchedUser.document;
        }

        this.applyBackendEligibilityFlags(response.data);
        this.applyBackendEligibilityFlags(fetchedUser);

        // Limpar o telefone da API para o formato que a máscara do frontend espera
        if (fetchedUser.phone) {
          let cleanPhone = fetchedUser.phone.replace(/\D/g, '');
          if (cleanPhone.startsWith('55') && cleanPhone.length > 2) {
              cleanPhone = cleanPhone.substring(2); 
          }
          fetchedUser.phone = cleanPhone; // Armazena o telefone limpo na store
        }

        this.user = { ...this.user, ...fetchedUser }; // merge: preserva id/role do JWT

        // Sincroniza avatar_url no lastLoginSession (qualquer dispositivo, próximo login)
        if (fetchedUser.avatar_url) {
          try {
            const raw = localStorage.getItem('lastLoginSession');
            if (raw) {
              const session = JSON.parse(raw);
              session.avatar_url = fetchedUser.avatar_url;
              localStorage.setItem('lastLoginSession', JSON.stringify(session));
            }
          } catch { /* ignora erros de localStorage */ }
        }
      } catch (error) {
        console.error('Erro ao carregar perfil:', error);
        this.profileError = 'Não foi possível carregar seus dados de perfil.';
        // Não zera o user em caso de erro — mantém os dados do JWT/localStorage
        // Não propaga o erro — profileError é suficiente para a UI; callers não tratam o throw
      } finally {
        this.profileLoading = false;
        this.profileLoaded = true; // Marca como carregado independentemente de sucesso ou erro
      }
    },

    async updateProfile(payload) {
      if (this.profileSaving || !this.user?.id) return;

      this.profileSaving = true;
      this.profileError = null;

      try {
        // Prepare o payload para enviar para a API (com +55)
        const apiPayload = { ...payload };
        if (apiPayload.phone) {
            apiPayload.phone = apiPayload.phone.replace(/\D/g, '');
            if (!apiPayload.phone.startsWith('55')) {
                apiPayload.phone = '55' + apiPayload.phone;
            }
            apiPayload.phone = '+' + apiPayload.phone;
        }
        const response = await api.put(`/users/${this.user.id}`, apiPayload); 
        
        // Supondo que a API retorne apenas uma mensagem de sucesso
        // Se ela retornar dados, você pode mesclar: const updatedUserData = response.data.user || response.data.profile || response.data;
        
        // *** CRÍTICO: Atualiza a store com o payload local, garantindo que o 'phone' esteja no formato limpo para o frontend ***
        const updatedUserLocalData = { ...payload };
        if (updatedUserLocalData.phone) {
            let cleanPhoneForStore = updatedUserLocalData.phone.replace(/\D/g, '');
            // Se já tem 55 e vem do payload que foi enviado para a API (com +55), removemos para a store
            if (cleanPhoneForStore.startsWith('55') && cleanPhoneForStore.length > 2) {
                cleanPhoneForStore = cleanPhoneForStore.substring(2);
            }
            updatedUserLocalData.phone = cleanPhoneForStore;
        }

        this.user = { ...this.user, ...updatedUserLocalData }; // Mescla o payload com os dados existentes

        // Sincroniza com a API para garantir que dados normalizados pelo backend sejam refletidos
        await this.fetchUserProfile();

        return true; 
      } catch (error) {
        console.error('Erro ao atualizar perfil do usuário na API:', error);
        this.profileError = error.response?.data?.message || 'Erro ao salvar perfil. Tente novamente.';
        throw error; 
      } finally {
        this.profileSaving = false;
      }
    },

  }
});